/**
 * This file was auto-generated by client-types. Do not manually edit this file.
 * Your changes would be automatically overwritten.
 *
 * Service Documentation: https://private.hubteam.com/shoebill/service/com.hubspot.customassistants.rpc.CustomAssistantsService
 */

import type {
  ChirpAvroRpcDetails,
  Requestify,
  unhandledEnumCase,
  Default,
  HubspotMetadata,
} from 'rpc-client-utils';

export type CausalChainLink = {
  coordinates: ChirpMethodCoordinates;
  errorClass: string;
  errorMessage?: string;
};

export type ChirpMethodCoordinates = { service: string; method: string };

export type ContextType =
  | 'CRM_OBJECT'
  | 'CUSTOM_ASSISTANT_USER_MEMORIES'
  | 'USER_MEMORIES'
  | 'PAGE_CONTEXT'
  | 'USER_ACTIVITY'
  | 'PORTAL_KNOWLEDGE'
  | unhandledEnumCase;

/**
 *  Represents a conversation starter with optional metadata.
 *  This allows for rich conversation starters with titles, descriptions, labels,
 *  and extensibility for future metadata fields.
 */
export type ConversationStarter = {
  id: number;
  text: string;
  title: string;
  description: string;
  labels: Default<string[]>;
};

export type ConversationStartersConfig = {
  static: Default<ConversationStarter[]>;
  providerFqcn?: string;
};

/**
 *  Custom Assistant - the new v2 model for portal-specific assistants
 */
export type CustomAssistant = {
  id: number;
  portalId: number;
  name?: string;
  welcomeMessage?: string;
  instructions?: string;
  setUpInstructions?: string;
  icon?: string;
  description?: string;
  type: Default<CustomAssistantType>;
  status: Default<CustomAssistantStatus>;
  enabledContext: Default<ContextType[]>;
  conversationStarters: Default<ConversationStarter[]>;
  conversationStartersConfig: Default<ConversationStartersConfig>;
  resources: Default<ResourceCollection>;
  enabledBuiltInTools: Default<string[]>;
  /** @deprecated */
  templateGlobalId?: number;
  templateGlobalIdv2?: string;
  templateVersion?: number;
  permissions?: CustomAssistantPermissionWithUserTeamPermissionsChirp;
  createdBy: number;
  updatedBy: number;
  createdAt: number;
  updatedAt: number;
  authorName?: string;
  templateName?: string;
  model: Default<Model>;
  conversationStartersProviderFqcn?: string;
};

export type CustomAssistantSortDirection = 'ASC' | 'DESC' | unhandledEnumCase;

export type CustomAssistantSortField =
  | 'NAME'
  | 'CREATED_AT'
  | 'UPDATED_AT'
  | unhandledEnumCase;

export type CustomAssistantStatus =
  | 'DRAFT'
  | 'ACTIVE'
  | 'INACTIVE'
  | unhandledEnumCase;

/**
 *  Summary view of a Custom Assistant for list responses
 *  Contains essential information without heavy details like full resource configurations
 */
export type CustomAssistantSummary = {
  id: number;
  portalId: number;
  name?: string;
  welcomeMessage?: string;
  setUpInstructions?: string;
  icon?: string;
  description?: string;
  status: CustomAssistantStatus;
  createdBy: number;
  updatedBy: number;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number;
  templateGlobalId?: string;
  enabledBuiltInTools?: string[];
  permissions?: CustomAssistantPermissionWithUserTeamPermissionsChirp;
  authorName?: string;
  templateName?: string;
};

export type CustomAssistantType =
  | 'PORTAL'
  | 'PUBLIC_TEMPLATE'
  | 'PRIVATE_TEMPLATE'
  | 'BZE_BACKED'
  | unhandledEnumCase;

export type CustomAssistantAccessType =
  | 'VIEW'
  | 'EDIT'
  | 'INSTALL_RUN'
  | unhandledEnumCase;

export type CustomAssistantEntityType = 'USER' | 'TEAM' | unhandledEnumCase;

/**
 *  CHIRP-safe version of CustomAssistantPermissionWithUserTeamPermissions that excludes portalId.
 *  The portalId is accessed from ChirpContext instead.
 */
export type CustomAssistantPermissionWithUserTeamPermissionsChirp = {
  customAssistantId: number;
  ownerId: number;
  permissionsMode: Default<CustomAssistantPermissionsMode>;
  userTeamPermissions: Default<CustomAssistantUserTeamPermission[]>;
};

export type CustomAssistantPermissionsMode =
  | 'OPEN'
  | 'ENFORCE_EDIT'
  | 'ENFORCE_EDIT_AND_RUN'
  | 'ENFORCE_ALL'
  | unhandledEnumCase;

export type CustomAssistantUserTeamPermission = {
  id?: number;
  customAssistantId: number;
  portalId: number;
  entityType: CustomAssistantEntityType;
  entityId: number;
  accessType: CustomAssistantAccessType;
};

/**
 *  Collection of resource configurations for a Custom Assistant
 */
export type ResourceCollection = {
  resourceConfigs: Default<Record<string, ResourceConfig>>;
};

/**
 *  Configuration for a specific resource type
 */
export type ResourceConfig = {
  type: ResourceType;
  ids: Default<string[]>;
  metadata?: ChirpServiceMetadata;
};

export type ResourceType =
  | 'DEFAULT_TOOL'
  | 'KNOWLEDGE_BOX'
  | 'CHIRP_SERVICE'
  | 'MCP_SERVER'
  | 'DYNAMIC_CONTEXT_SERVICE'
  | 'SUB_AGENT'
  | unhandledEnumCase;

export type ChirpServiceMetadata = {
  toolsRequiringConfirmation: Default<string[]>;
};

/**
 *  Request to create a new custom assistant
 */
export type CreateCustomAssistantRequest = {
  name: string;
  welcomeMessage: string;
  instructions: string;
  setUpInstructions?: string;
  icon?: string;
  description?: string;
  status: CustomAssistantStatus;
  enabledContext: Default<ContextType[]>;
  conversationStarters: Default<ConversationStarter[]>;
  conversationStartersConfig: Default<ConversationStartersConfig>;
  model: Default<Model>;
  resources: ResourceCollection;
  enabledBuiltInTools?: string[];
  permissions?: CustomAssistantPermissionWithUserTeamPermissionsCreateChirp;
  feedbackCorrelationId?: string;
};

/**
 *  Response for creating a new custom assistant
 */
export type CreateCustomAssistantResponse = {
  customAssistant: CustomAssistant;
};

export type CrmObjectContext = { objectTypeId: string; objectId: number };

/**
 *  Cursor for custom assistants pagination
 */
export type CustomAssistantCursor = {
  offset: Default<number>;
  stringOffset?: string;
};

export type CustomAssistantError = {
  type: ErrorType;
  chirpErrorMessage?: string;
  coordinates?: ChirpMethodCoordinates;
  causalChain: Default<CausalChainLink[]>;
  shouldRetry: Default<boolean>;
};

/**
 *  Request to delete a custom assistant
 */
export type DeleteCustomAssistantRequest = {
  id: number;
  feedbackCorrelationId?: string;
};

/**
 *  Response for deleting a custom assistant
 */
export type DeleteCustomAssistantResponse = { success: boolean };

/**
 *  Request to duplicate an existing custom assistant
 */
export type DuplicateCustomAssistantRequest = { id: number };

/**
 *  Response for duplicating a custom assistant
 */
export type DuplicateCustomAssistantResponse = {
  customAssistant: CustomAssistant;
};

export type FindAssistantsByResourceIdRequest = {
  resourceType: ResourceType;
  resourceId: string;
  pageLength: Default<number>;
  offset: CustomAssistantCursor;
};

export type GetConversationStartersRequest = {
  customAssistantId: number;
  threadId: number;
  pageContext?: PageContext;
  crmObjectContext?: CrmObjectContext;
};

export type GetConversationStartersResponse = {
  conversationStarters: Default<ConversationStarter[]>;
};

/**
 *  Request to get a custom assistant by ID
 */
export type GetCustomAssistantRequest = { id: number };

/**
 *  Response for getting a custom assistant by ID
 */
export type GetCustomAssistantResponse = { customAssistant: CustomAssistant };

/**
 *  Request to install a custom assistant template using its global ID
 */
export type InstallCustomAssistantTemplateFromGlobalIdRequest = {
  templateGlobalId: string;
};

/**
 *  Response for installing a custom assistant template using its global ID
 */
export type InstallCustomAssistantTemplateFromGlobalIdResponse = {
  customAssistant: CustomAssistant;
};

/**
 *  Request to list custom assistants with pagination support
 */
export type ListCustomAssistantsRequest = {
  pageLength: Default<number>;
  searchTerm?: string;
  filterByPermissions: Default<boolean>;
  authorName?: string;
  filterByAuthor?: boolean;
  updatedBy?: number;
  sortField: Default<CustomAssistantSortField>;
  sortDirection: Default<CustomAssistantSortDirection>;
  offset: CustomAssistantCursor;
};

/**
 *  Response for listing custom assistants with pagination
 */
export type ListCustomAssistantsResponse = {
  results: Default<CustomAssistantSummary[]>;
  offset: CustomAssistantCursor;
  hasMore: boolean;
};

/**
 *  Request to lookup a user's installed assistant by global template ID
 */
export type LookupUserInstalledAssistantByGlobalIdRequest = {
  globalId: string;
};

/**
 *  Response for looking up a user's installed assistant by global template ID
 */
export type LookupUserInstalledAssistantByGlobalIdResponse = {
  installedAssistant?: CustomAssistant;
};

export type PageContext = {
  url?: string;
  title?: string;
  sourceHostApp?: string;
};

/**
 *  Request to submit user feedback on a generated prompt/instruction for a custom assistant.
 *  This feedback is used to improve prompt generation quality over time.
 */
export type SubmitPromptFeedbackRequest = {
  customAssistantId: number;
  customAssistantTemplateGlobalId?: string;
  generatedPrompt: string;
  positive: boolean;
  explicit: boolean;
  feedback?: string;
  correlationId: string;
};

/**
 *  Request to update an existing custom assistant
 */
export type UpdateCustomAssistantRequest = {
  id: number;
  name: string;
  welcomeMessage: string;
  instructions: string;
  setUpInstructions?: string;
  icon?: string;
  description?: string;
  status: CustomAssistantStatus;
  enabledContext: Default<ContextType[]>;
  conversationStarters: Default<ConversationStarter[]>;
  conversationStartersConfig: Default<ConversationStartersConfig>;
  model?: Model;
  resources: ResourceCollection;
  enabledBuiltInTools?: string[];
  permissions?: CustomAssistantPermissionWithUserTeamPermissionsChirp;
  feedbackCorrelationId?: string;
};

/**
 *  Response for updating a custom assistant
 */
export type UpdateCustomAssistantResponse = {
  customAssistant: CustomAssistant;
};

export type ErrorType =
  | 'HIPAA_PORTAL_ACCESS_DENIED'
  | 'PERMISSION_DENIED'
  | 'ASSISTANT_NOT_FOUND'
  | 'UPDATE_FAILURE'
  | 'INVALID_TYPE'
  | 'TEMPLATE_NOT_FOUND'
  | 'TEMPLATE_UPDATE_FAILURE'
  | 'TEMPLATE_ALREADY_INSTALLED'
  | 'TEMPLATE_HAS_ACTIVE_INSTALLATIONS'
  | 'TEMPLATE_PROMOTION_FAILURE'
  | 'INVALID_MODEL'
  | 'INVALID_METADATA'
  | 'WRONG_HUBLET'
  | 'FEATURE_NOT_AVAILABLE'
  | unhandledEnumCase;

/**
 *  CHIRP-safe version of CustomAssistantPermissionWithUserTeamPermissions for creation requests.
 *  This interface is used during custom assistant creation when the CustomAssistantId doesn't exist yet.
 *  The portalId is accessed from ChirpContext, and the CustomAssistantId will be set after creation.
 */
export type CustomAssistantPermissionWithUserTeamPermissionsCreateChirp = {
  ownerId: number;
  permissionsMode: Default<CustomAssistantPermissionsMode>;
  userTeamPermissions: Default<
    CustomAssistantUserTeamPermissionCreateRequest[]
  >;
};

export type CustomAssistantUserTeamPermissionCreateRequest = {
  entityType: CustomAssistantEntityType;
  entityId: number;
  accessType: CustomAssistantAccessType;
};

export type VoidResponse = {};

export type Model =
  | 'SUPPORT_BOT_QUERY_ADAPTATION'
  | 'PHI_3_5_VISION_INSTRUCT'
  | 'HUBSPOT_LLM_SMALL'
  | 'HUBSPOT_LLM_LARGE'
  | 'CLAUDE_4_SONNET'
  | 'CLAUDE_4_OPUS'
  | 'CLAUDE_4_5_SONNET'
  | 'CLAUDE_4_5_HAIKU'
  | 'CLAUDE_4_5_OPUS'
  | 'CLAUDE_4_6_SONNET'
  | 'CLAUDE_4_6_OPUS'
  | 'CLAUDE_5_5_HAIKU'
  | 'CLAUDE_5_SONNET'
  | 'CLAUDE_5_5_SONNET'
  | 'GPT_4_O_2024_05_13'
  | 'GPT_4_O_2024_08_06'
  | 'GPT_4_O_2024_11_20'
  | 'GPT_4_O'
  | 'GPT_4_O_MINI_2024_07_18'
  | 'GPT_4_O_MINI'
  | 'GPT_4_1_2025_04_14'
  | 'GPT_4_1'
  | 'GPT_4_1_MINI_2025_04_14'
  | 'GPT_4_1_MINI'
  | 'GPT_4_1_NANO_2025_04_14'
  | 'GPT_4_1_NANO'
  | 'O_3_MINI_2025_01_31'
  | 'O_3_MINI'
  | 'O_3_2025_04_16'
  | 'O_3'
  | 'GPT_5_NANO_2025_08_07'
  | 'GPT_5_NANO'
  | 'GPT_5_MINI_2025_08_07'
  | 'GPT_5_MINI'
  | 'GPT_5_2025_08_07'
  | 'GPT_5'
  | 'GPT_5_1_2025_11_13'
  | 'GPT_5_1'
  | 'GPT_5_2_2025_12_11'
  | 'GPT_5_2'
  | 'GPT_5_4_2026_03_05'
  | 'GPT_5_4'
  | 'GPT_5_4_MINI_2026_03_17'
  | 'GPT_5_4_MINI'
  | 'GPT_5_4_NANO_2026_03_17'
  | 'GPT_5_4_NANO'
  | 'GPT_5_5_2026_04_24'
  | 'GPT_5_5'
  | 'GPT_5_6_LUNA'
  | 'GPT_6_LUNA'
  | 'GEMINI_2_5_PRO'
  | 'GEMINI_2_5_FLASH'
  | 'GEMINI_2_5_FLASH_LITE'
  | 'GEMINI_3_FLASH'
  | 'GEMINI_3_1_PRO'
  | 'GEMINI_3_1_FLASH_LITE'
  | 'GEMINI_3_5_FLASH'
  | 'GEMINI_3_5_FLASH_LITE'
  | 'GEMINI_3_7_FLASH'
  | 'GEMINI_3_8_FLASH'
  | 'GLM_5_3_FLASH'
  | 'GLM_5_3'
  | unhandledEnumCase;

export type GetCustomAssistantRequestType =
  Requestify<GetCustomAssistantRequest>;
export type GetCustomAssistantResponseType = GetCustomAssistantResponse;
export type GetCustomAssistantErrorType = CustomAssistantError &
  HubspotMetadata;
export const getCustomAssistantRpc: ChirpAvroRpcDetails<
  GetCustomAssistantRequestType,
  GetCustomAssistantResponseType,
  GetCustomAssistantErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'getCustomAssistant',
  auth: 'app',
  fingerprint: 'kAz63WI73cCEXQreR375wjanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetConversationStartersRequestType =
  Requestify<GetConversationStartersRequest>;
export type GetConversationStartersResponseType =
  GetConversationStartersResponse;
export type GetConversationStartersErrorType = CustomAssistantError &
  HubspotMetadata;
export const getConversationStartersRpc: ChirpAvroRpcDetails<
  GetConversationStartersRequestType,
  GetConversationStartersResponseType,
  GetConversationStartersErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'getConversationStarters',
  auth: 'app',
  fingerprint: 'eJAUqD86GiE_I42kP2itrTanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type ListCustomAssistantsRequestType =
  Requestify<ListCustomAssistantsRequest>;
export type ListCustomAssistantsResponseType = ListCustomAssistantsResponse;
export type ListCustomAssistantsErrorType = CustomAssistantError &
  HubspotMetadata;
export const listCustomAssistantsRpc: ChirpAvroRpcDetails<
  ListCustomAssistantsRequestType,
  ListCustomAssistantsResponseType,
  ListCustomAssistantsErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'listCustomAssistants',
  auth: 'app',
  fingerprint: 'Eaq2_6bBR5Wbftlo4_LWfDanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type CreateCustomAssistantRequestType =
  Requestify<CreateCustomAssistantRequest>;
export type CreateCustomAssistantResponseType = CreateCustomAssistantResponse;
export type CreateCustomAssistantErrorType = CustomAssistantError &
  HubspotMetadata;
export const createCustomAssistantRpc: ChirpAvroRpcDetails<
  CreateCustomAssistantRequestType,
  CreateCustomAssistantResponseType,
  CreateCustomAssistantErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'createCustomAssistant',
  auth: 'app',
  fingerprint: 'RKg4LOmqftarCBJflj4gEDanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type DuplicateCustomAssistantRequestType =
  Requestify<DuplicateCustomAssistantRequest>;
export type DuplicateCustomAssistantResponseType =
  DuplicateCustomAssistantResponse;
export type DuplicateCustomAssistantErrorType = CustomAssistantError &
  HubspotMetadata;
export const duplicateCustomAssistantRpc: ChirpAvroRpcDetails<
  DuplicateCustomAssistantRequestType,
  DuplicateCustomAssistantResponseType,
  DuplicateCustomAssistantErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'duplicateCustomAssistant',
  auth: 'app',
  fingerprint: 'uMKJE-PN0FhA-W6zxtcCgDanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type UpdateCustomAssistantRequestType =
  Requestify<UpdateCustomAssistantRequest>;
export type UpdateCustomAssistantResponseType = UpdateCustomAssistantResponse;
export type UpdateCustomAssistantErrorType = CustomAssistantError &
  HubspotMetadata;
export const updateCustomAssistantRpc: ChirpAvroRpcDetails<
  UpdateCustomAssistantRequestType,
  UpdateCustomAssistantResponseType,
  UpdateCustomAssistantErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'updateCustomAssistant',
  auth: 'app',
  fingerprint: 'ZMxP5HrQXgQYHMjOlNDwFDanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type DeleteCustomAssistantRequestType =
  Requestify<DeleteCustomAssistantRequest>;
export type DeleteCustomAssistantResponseType = DeleteCustomAssistantResponse;
export type DeleteCustomAssistantErrorType = CustomAssistantError &
  HubspotMetadata;
export const deleteCustomAssistantRpc: ChirpAvroRpcDetails<
  DeleteCustomAssistantRequestType,
  DeleteCustomAssistantResponseType,
  DeleteCustomAssistantErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'deleteCustomAssistant',
  auth: 'app',
  fingerprint: 'WCuOR3OA9j77e6bFusux0TanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type InstallCustomAssistantTemplateFromGlobalIdRequestType =
  Requestify<InstallCustomAssistantTemplateFromGlobalIdRequest>;
export type InstallCustomAssistantTemplateFromGlobalIdResponseType =
  InstallCustomAssistantTemplateFromGlobalIdResponse;
export type InstallCustomAssistantTemplateFromGlobalIdErrorType =
  CustomAssistantError & HubspotMetadata;
export const installCustomAssistantTemplateFromGlobalIdRpc: ChirpAvroRpcDetails<
  InstallCustomAssistantTemplateFromGlobalIdRequestType,
  InstallCustomAssistantTemplateFromGlobalIdResponseType,
  InstallCustomAssistantTemplateFromGlobalIdErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'installCustomAssistantTemplateFromGlobalId',
  auth: 'app',
  fingerprint: 'F2ItRzL0NWOHeJYOvrg2CTanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type LookupUserInstalledAssistantByGlobalIdRequestType =
  Requestify<LookupUserInstalledAssistantByGlobalIdRequest>;
export type LookupUserInstalledAssistantByGlobalIdResponseType =
  LookupUserInstalledAssistantByGlobalIdResponse;
export type LookupUserInstalledAssistantByGlobalIdErrorType =
  CustomAssistantError & HubspotMetadata;
export const lookupUserInstalledAssistantByGlobalIdRpc: ChirpAvroRpcDetails<
  LookupUserInstalledAssistantByGlobalIdRequestType,
  LookupUserInstalledAssistantByGlobalIdResponseType,
  LookupUserInstalledAssistantByGlobalIdErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'lookupUserInstalledAssistantByGlobalId',
  auth: 'app',
  fingerprint: 'FUsSZduaZJZ_3NXo29FttzanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type FindAssistantsByResourceIdRequestType =
  Requestify<FindAssistantsByResourceIdRequest>;
export type FindAssistantsByResourceIdResponseType =
  ListCustomAssistantsResponse;
export type FindAssistantsByResourceIdErrorType = CustomAssistantError &
  HubspotMetadata;
export const findAssistantsByResourceIdRpc: ChirpAvroRpcDetails<
  FindAssistantsByResourceIdRequestType,
  FindAssistantsByResourceIdResponseType,
  FindAssistantsByResourceIdErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'findAssistantsByResourceId',
  auth: 'app',
  fingerprint: '4RM8nyj3tpKbftlo4_LWfDanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type SubmitPromptFeedbackRequestType =
  Requestify<SubmitPromptFeedbackRequest>;
export type SubmitPromptFeedbackResponseType = VoidResponse;
export type SubmitPromptFeedbackErrorType = CustomAssistantError &
  HubspotMetadata;
export const submitPromptFeedbackRpc: ChirpAvroRpcDetails<
  SubmitPromptFeedbackRequestType,
  SubmitPromptFeedbackResponseType,
  SubmitPromptFeedbackErrorType,
  false
> = {
  serviceName: 'com.hubspot.customassistants.rpc.CustomAssistantsService',
  rpcName: 'submitPromptFeedback',
  auth: 'app',
  fingerprint: 'rKThYVdALqHf-iFjiiqpOTanqYkRlVxV',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};
